import { ProductStateCode } from '@gooduncles/gu-app-schema';
import { ColDef, ColGroupDef, GridApi } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { Checkbox, notification } from 'antd';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { productInventoryTableTooltipMap } from 'src/constants';

import {
  getRowStyleForProductInventory,
  inventoryQuantityFormatter,
  onCellValueChangedWithUpdate,
  onValueSetterWithValidation,
} from 'src/utils/ag-grid-util';
import { errorObjectToString } from 'src/utils/common';
import { updateProductInventory, updateProductInventorySupplierId } from 'src/utils/firebase/firebase-short-cut';

import useProductInventoriesWithDetail, {
  ProductInventoryWithDetail,
} from 'src/hooks/subscription/useProductInventoriesWithDetail';
import { useTitle } from 'src/hooks/useTitle';

import AgCellDeleteInventory from 'src/atomic-components/atoms/AgCell/AgCellDeleteInventory';
import AgCellProductSupplierSelect from 'src/atomic-components/atoms/AgCell/AgCellProductSupplierSelect';
import AgCellToggleEditor from 'src/atomic-components/atoms/AgCell/AgCellToggleEditor';
import InventoryDetailTabs from 'src/atomic-components/organisms/InventoryDetailTabs/InventoryDetailTabs';

type RowData = ProductInventoryWithDetail;

const productInventoryPath = 'productInventory';
const onCellValueChanged = onCellValueChangedWithUpdate<RowData>('재고 관리');

const defaultColDef: ColDef<RowData> = {
  sortable: true,
  resizable: true,
  filter: true,
  floatingFilter: true,
};

const ProductInventoryBoard: FC = () => {
  useTitle('재고 관리');
  const { productInventories: rowData, productSuppliers } = useProductInventoriesWithDetail();
  const discontinuedProductInventories = rowData.filter((row) =>
    row.products.some((product) => product.state === ProductStateCode.DISCONTINUED)
  );
  const [showBarcode, setShowBarcode] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [selectedRow, setSelectedRow] = useState<RowData | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { inventoryId } = useParams();
  const gridApiRef = useRef<GridApi>();
  const isInitialMount = useRef(true);

  const onChangeShowBarcode = (e: CheckboxChangeEvent) => {
    setShowBarcode(e.target.checked);
  };

  const onChangeShowInactive = (e: CheckboxChangeEvent) => {
    setShowInactive(e.target.checked);
  };

  useEffect(() => {
    if (inventoryId && rowData && gridApiRef.current) {
      const inventory = rowData.find((row) => row._id === inventoryId);
      if (inventory) {
        setSelectedRow(inventory);
        gridApiRef.current.forEachNode((node) => {
          if (node.data?._id === inventoryId) {
            node.setSelected(true);
            if (isInitialMount.current) {
              gridApiRef.current?.ensureNodeVisible(node, 'middle');
              isInitialMount.current = false;
            }
          }
        });
      }
    }
  }, [inventoryId, rowData]);

  const columnDefs: (ColDef<RowData> | ColGroupDef<RowData>)[] = useMemo(
    () => [
      {
        headerName: 'inventoryId',
        field: '_id',
        width: 240,
        hide: true,
      },
      {
        headerName: '재고명',
        field: 'inventoryName',
        width: 280,
        editable: true,
        onCellValueChanged: (params) => onCellValueChanged(params, productInventoryPath, params.data._id),
      },
      {
        headerName: '거래처',
        field: 'supplier.name',
        width: 120,
        editable: true,
        cellRenderer: (params: any) => params.value,
        cellEditor: (params: any) => (
          <AgCellProductSupplierSelect
            {...params}
            productSuppliers={productSuppliers ?? []}
            onUpdate={async (supplierId) => {
              try {
                await updateProductInventorySupplierId(params.data._id, supplierId);
              } catch (error) {
                const description = errorObjectToString(error);
                notification.error({
                  message: '거래처 변경에 실패했습니다.',
                  description,
                });
              }
            }}
          />
        ),
      },
      {
        headerName: '재고 수량',
        field: 'quantity',
        width: 120,
        valueFormatter: inventoryQuantityFormatter,
        type: 'numericColumn',
      },
      {
        headerName: 'EA',
        field: 'uom.ea',
        children: [
          {
            headerName: '비율',
            field: 'uom.ea.ratio',
            width: showBarcode ? 88 : 100,
            headerTooltip: productInventoryTableTooltipMap.uomEaRatio,
            editable: true,
            valueSetter: (params) => onValueSetterWithValidation(params, ['_id'], undefined, { type: 'number' }),
            onCellValueChanged: (params) => onCellValueChanged(params, productInventoryPath, params.data._id),
          },
          {
            headerName: '바코드',
            field: 'uom.ea.barcode',
            width: 150,
            hide: !showBarcode,
            editable: true,
            onCellValueChanged: (params) => onCellValueChanged(params, productInventoryPath, params.data._id),
          },
        ],
      },
      {
        headerName: 'BUNDLE',
        field: 'uom.bundle',
        children: [
          {
            headerName: '비율',
            field: 'uom.bundle.ratio',
            width: showBarcode ? 88 : 100,
            headerTooltip: productInventoryTableTooltipMap.uomBundleRatio,
            editable: true,
            valueSetter: (params) => onValueSetterWithValidation(params, ['_id'], undefined, { type: 'number' }),
            onCellValueChanged: (params) => onCellValueChanged(params, productInventoryPath, params.data._id),
          },
          {
            headerName: '바코드',
            field: 'uom.bundle.barcode',
            width: 150,
            hide: !showBarcode,
            editable: true,
            onCellValueChanged: (params) => onCellValueChanged(params, productInventoryPath, params.data._id),
          },
        ],
      },
      {
        headerName: '삭제',
        width: 120,
        cellRenderer: (params: any) => (
          <AgCellDeleteInventory
            id={params.data._id}
            disabled={params.data.products.length > 0 || params.data.deletedAt !== null}
          />
        ),
        valueGetter: (params: any) => {
          const data = params.data as RowData;
          return data.products.length > 0 ? '삭제 불가' : '삭제 가능';
        },
      },
      {
        headerName: '사용 ⓘ',
        field: 'active',
        headerTooltip: '미사용 처리시 재고를 사용하는 상품 모두 판매중지 처리됩니다.\n(사용은 관계없음)',
        width: 96,
        filterParams: {
          valueFormatter: (params: any) => (params.value ? '사용' : '미사용'),
        },
        cellRenderer: (params: any) => (
          <AgCellToggleEditor
            id={params.data._id}
            value={params.data.active}
            field='active'
            size='small'
            onChange={updateProductInventory}
          />
        ),
      },
    ],
    [showBarcode, productSuppliers]
  );

  return (
    <div className='ag-theme-alpine height100 flexColumn'>
      <div className='flexRow flexSpaceBetween'>
        <Checkbox checked={showBarcode} onChange={onChangeShowBarcode}>
          바코드 표시
        </Checkbox>
        <Checkbox checked={showInactive} onChange={onChangeShowInactive}>
          미사용 상품 재고만 표시({discontinuedProductInventories.length})
        </Checkbox>
      </div>
      <div className='flexRow height100 width100 overflowHidden'>
        {/* 900px */}
        <div className='flex2 overflowAuto'>
          <AgGridReact
            rowData={showInactive ? discontinuedProductInventories : rowData}
            // rowId를 지정해주지 않으면 데이터 변경시 refresh가 발생한다.
            getRowId={(params) => params.data._id}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            tooltipShowDelay={500}
            getRowStyle={getRowStyleForProductInventory}
            // 편집 완료후 스크롤 이동을 막는다.
            suppressScrollOnNewData={true}
            alwaysShowHorizontalScroll={true}
            // 행 선택
            rowSelection='single'
            onGridReady={(params) => {
              gridApiRef.current = params.api;
              if (inventoryId) {
                params.api.forEachNode((node) => {
                  if (node.data?._id === inventoryId) {
                    node.setSelected(true);
                  }
                });
              }
            }}
            onSelectionChanged={(e) => {
              const selectedRow = e.api.getSelectedRows()[0];
              setSelectedRow(selectedRow);
              if (selectedRow) {
                navigate(`/inventory/settings/${selectedRow._id}`);
              } else {
                navigate('/inventory/settings');
              }
            }}
            onCellClicked={(params) => {
              const colId = params.column.getColId();
              if (colId.startsWith('uom.ea')) {
                setSearchParams({ tab: 'ea' });
              } else if (colId.startsWith('uom.bundle')) {
                setSearchParams({ tab: 'bundle' });
              }
            }}
            // 행 선택을 켜놓더라도 개별 셀의 내용을 복사할 수 있도록한다.
            enableCellTextSelection={true}
          />
        </div>
        <div className='flex1 overflowAuto'>
          {selectedRow && (
            <InventoryDetailTabs
              inventory={selectedRow}
              products={selectedRow.products}
              tab={searchParams.get('tab') ?? 'ea'}
              onTabChange={(tab) => setSearchParams({ tab })}
              inventories={rowData}
              productSuppliers={productSuppliers}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductInventoryBoard;
